/**
 * La frontera de régimen de cadencia, en un solo lugar.
 *
 * ## Por qué existe este módulo
 *
 * Hasta el 07/10/2026 había dos fronteras. `measureCadence` llamaba LOW a todo
 * lo que pasara de 6 s, y `selectProfile` aplicaba el perfil MEDIUM hasta 7 s.
 * Un equipo de 6,5 s en marcha se calculaba con los umbrales de 5 s, pero el
 * informe le ponía el aviso de «cadencia baja», y en la comparación de flota
 * —que agrupa por perfil— su grupo podía salir rotulado LOW y el diagnóstico
 * contaba dos cadencias como una. El ADR-011 lo dejó registrado como pendiente.
 * Además, el muestreo decidía con la mediana sin redondear y `selectProfile` con
 * la redondeada: entre 2,995 y 3 s el rótulo era HIGH y el perfil MEDIUM.
 *
 * Se conserva la regla de `selectProfile`, que es la que decide los umbrales:
 * con ella se guardaron los perfiles activos y se simularon las calibraciones
 * de LOW y MEDIUM del 06–07/10. La de `measureCadence` solo rotulaba, así que
 * ningún vehículo cambia de perfil, de umbrales ni de grupo de flota.
 *
 * ## Lo que no resuelve
 *
 * Un equipo que en marcha reporta entre 6 y 7 s puede caer en MEDIUM un período
 * y en LOW el siguiente. Estabilizarlo es una regla nueva y está como candidata
 * en el ADR-012; este módulo no la aplica.
 *
 * Puro: sin red, sin reloj, sin dependencias en tiempo de ejecución.
 */

import type { CadenceRegime } from "./profiles.ts";

/** Por debajo, alta frecuencia (HIGH). Desde aquí, MEDIUM. */
export const CADENCE_REGIME_HIGH_MEDIUM_SECONDS = 3;

/** Por debajo, MEDIUM. Desde aquí, LOW. Es el corte de `selectProfile`. */
export const CADENCE_REGIME_MEDIUM_LOW_SECONDS = 7;

/**
 * Régimen de una cadencia en marcha medida, en segundos.
 *
 * Intervalos semiabiertos, iguales a los `appliesToIntervalSeconds` de los
 * perfiles: HIGH `[0, 3)`, MEDIUM `[3, 7)`, LOW `[7, ∞)`. Por encima del techo
 * de maniobras (30 s) el régimen sigue siendo LOW, porque `selectProfile`
 * aplica el perfil de 10 s para la velocidad sostenida.
 *
 * Quien la llame con la cadencia que publica `measureCadence` y quien llame a
 * `selectProfile` con esa misma cifra obtienen el mismo régimen.
 */
export function regimeForCadence(seconds: number): CadenceRegime {
  if (seconds < CADENCE_REGIME_HIGH_MEDIUM_SECONDS) return "HIGH";
  if (seconds < CADENCE_REGIME_MEDIUM_LOW_SECONDS) return "MEDIUM";
  return "LOW";
}
